import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '../../components/PageHeader';
import { Tabs, type TabDef } from '../../components/Tabs';
import { useFilterState } from '../../components/filters/useFilterState';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import ChatQuestionsFilterBar from './ChatQuestionsFilterBar';
import { DEFAULT_FILTERS, FILTER_STORAGE_KEY, type ChatQuestionsFilters } from './chatQuestionsFilters';
import GapsTab from './GapsTab';
import TurnsTab from './TurnsTab';

const TABS: TabDef[] = [
  { key: 'turns', label: 'Turns' },
  { key: 'gaps', label: 'Gaps' },
];

export default function ChatQuestions() {
  useDocumentTitle('Chat questions');
  // The tab and the gap key live in the URL so a gap's turn list is a shareable link.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'gaps' ? 'gaps' : 'turns';
  const gapKey = searchParams.get('gapKey') || null;
  const [page, setPage] = useState(1);

  // The queries read the debounced copy, so typing a project id does not fire a request
  // per keystroke. useFilterState emits once on mount without delay, then debounced.
  const [appliedFilters, setAppliedFilters] = useState<ChatQuestionsFilters | null>(null);
  const { filters, setFilters } = useFilterState<ChatQuestionsFilters>({
    storageKey: FILTER_STORAGE_KEY,
    defaults: DEFAULT_FILTERS,
    onFilterChange: setAppliedFilters,
    // A linked gap opens on every turn of that gap, not on the coverage left in this session.
    overrides: gapKey ? { coverage: [] } : undefined,
  });
  const queryFilters = appliedFilters ?? filters;

  const updateParams = (apply: (next: URLSearchParams) => void) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      apply(next);
      return next;
    });
  };

  // A filter change re-selects the population, so the current page number is meaningless.
  const handleFiltersChange = (patch: Partial<ChatQuestionsFilters>) => {
    setFilters({ ...filters, ...patch });
    setPage(1);
  };

  const handleTabChange = (key: string) => {
    updateParams((next) => {
      if (key === 'gaps') next.set('tab', 'gaps');
      else next.delete('tab');
    });
  };

  // Open every turn of the gap: the coverage filter would otherwise hide some of the rows
  // the gap's count includes. Apply it at once so the Turns query does not first run on the
  // stale coverage while the debounce is pending.
  const handleSelectGap = (key: string) => {
    const next = { ...filters, coverage: [] };
    setFilters(next);
    setAppliedFilters(next);
    setPage(1);
    updateParams((params) => {
      params.delete('tab');
      params.set('gapKey', key);
    });
  };

  const handleClearGapKey = () => {
    setPage(1);
    updateParams((params) => params.delete('gapKey'));
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <PageHeader
        title="Chat questions"
        description="Project chat turns, with the data the chat could not find"
        icon={<ChatBubbleLeftRightIcon className="size-6" />}
      />

      <Tabs tabs={TABS} active={activeTab} onChange={handleTabChange} />

      <ChatQuestionsFilterBar filters={filters} onChange={handleFiltersChange} />

      {activeTab === 'turns' ? (
        <TurnsTab
          filters={queryFilters}
          gapKey={gapKey}
          onClearGapKey={handleClearGapKey}
          page={page}
          onPageChange={setPage}
        />
      ) : (
        <GapsTab filters={queryFilters} onSelectGap={handleSelectGap} />
      )}
    </div>
  );
}
